import { Transaction, type TransactionList } from '@kalos/kalos-rpc';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  transactionStatusNeedsReviewAndPendingValidation,
  transactionStatusNeedsReviewAndValidated,
} from '../../modules/Transactions/components/constants';
import { type AdminTransactionFilter } from '../../modules/Transactions/components/TransactionAdmin';
import { trailingZero, TransactionClientService } from '../../tools/helpers';
import { queryKeys } from './constants';

export const applyFilters = ({
  obj,
  filters,
  isSU,
  departmentIDList,
}: {
  obj: Transaction;
  filters: AdminTransactionFilter;
  isSU: boolean;
  departmentIDList?: string;
  includeLineItems?: boolean;
}) => {
  obj.isActive = 1;
  if (departmentIDList && !isSU) {
    obj.departmentIdList = departmentIDList;
  }
  if (filters.userID && filters.userID != 0) {
    //here they want to see someone specific,
    //if transactions don't have departments, this conflicts
    obj.ownerId = filters.userID;
    obj.departmentIdList = '';
  }
  if (filters.costCenterID) {
    obj.costCenterId = filters.costCenterID;
  }
  if (filters.accountValue && Number(filters.accountValue)) {
    obj.artificialId = `%${filters.accountValue}-%`;
  }
  if (filters.departmentID && filters.departmentID !== 0) {
    obj.departmentId = filters.departmentID;
  }
  if (filters.includeLineItems) {
    obj.includeTransactionLineItems = true;
  }
  if (filters.includeTransactionValidation) {
    obj.includeTransactionValidation = true;
  }
  if (filters)
    if (filters.dateCreated) {
      obj.timestamp = `${filters.yearCreated}-${trailingZero(filters.dateCreated)}%`;
    } else if (filters.yearCreated) {
      obj.timestamp = `${filters.yearCreated}-%`;
    }
  if (filters.withoutLimit) {
    obj.withoutLimit = true;
  }
  if (filters.searchValue) {
    obj.vendor = `%${filters.searchValue}%`;
  }
  if (filters.statusID) {
    switch (Number(filters.statusID)) {
      case 5:
        obj.isRecorded = true;
        break;
      case 6:
        obj.isAudited = true;
        break;
      case 7:
        obj.isAudited = false;
        obj.fieldMask = ['IsAudited'];
        obj.statusId = 3;
        break;
      case 8:
        obj.isRecorded = false;
        obj.fieldMask = ['IsRecorded'];
        obj.statusId = 3;
        break;
      case 9:
        obj.isRecorded = false;
        obj.fieldMask = ['IsRecorded'];
        obj.notEquals = ['IsAudited'];
        obj.isAudited = true;
        obj.statusId = 3;
        break;
      case 10:
        obj.isRecorded = false;
        obj.fieldMask = ['IsRecorded'];
        obj.isAudited = true;
        break;
      case 11:
        obj.isRecorded = true;
        obj.isAudited = true;
        break;
      case 12:
        obj.isActive = 0;
        obj.fieldMask = obj.fieldMask.concat(['IsActive']);
        break;
      case transactionStatusNeedsReviewAndPendingValidation:
        obj.statusId = 2;
        obj.preValidated = false;
        obj.fieldMask = obj.fieldMask.concat(['PreValidated']);
        break;
      case transactionStatusNeedsReviewAndValidated:
        obj.statusId = 2;
        obj.preValidated = true;
        break;
      default:
        obj.statusId = Number(filters.statusID);
        break;
    }
  }

  obj.orderBy = filters.sort.sortBy;
  obj.orderDir = filters.sort.sortDir;
  return obj;
};

export const useTransactionAdminQuery = <TSelectData = TransactionList,>({
  filter,
  page,
  enabled = true,
  isSU,
  departmentIDList,
  select,
}: {
  filter: AdminTransactionFilter;
  page: number;
  enabled?: boolean;
  isSU: boolean;
  departmentIDList?: string;
  select?: (data: TransactionList) => TSelectData;
}) => {
  const requestObject = useMemo(() => {
    let reqObj = Transaction.create();
    reqObj = applyFilters({
      obj: reqObj,
      filters: filter,
      isSU,
      departmentIDList,
    });
    reqObj.pageNumber = page;
    reqObj.notEquals = reqObj.notEquals.concat(['VendorCategory']);
    reqObj.vendorCategory = "'Receipt','PickTicket','Invoice'";
    return reqObj;
  }, [departmentIDList, filter, isSU, page]);

  return useQuery({
    queryKey: [
      queryKeys.transaction.root,
      queryKeys.transaction.list,
      queryKeys.transaction.getTransactionHash(requestObject),
    ],
    queryFn: () => {
      return TransactionClientService.BatchGet(requestObject);
    },
    enabled,
    select,
  });
};
